'use client';

import React, { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { ArticleCard } from '@/components/article-card';
import { type AuthorProfile, type Article } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface AuthorArticlesClientProps {
  author: AuthorProfile;
  articles: Article[];
}

export function AuthorArticlesClient({ author, articles }: AuthorArticlesClientProps) {
  const searchParams = useSearchParams();
  const activeCategory = searchParams.get('category');

  const categories = useMemo(() => {
    return Array.from(new Set(articles.map(a => a.primaryCategory).filter(Boolean))) as string[];
  }, [articles]);

  const filteredArticles = useMemo(() => {
    const base = activeCategory 
      ? articles.filter(a => a.primaryCategory === activeCategory)
      : articles;
    
    return base.sort((a, b) => 
      new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime()
    );
  }, [articles, activeCategory]);

  const firstName = (author.name || 'Anonymous').split(' ')[0];

  return (
    <div className="w-full">
      {categories.length > 1 && (
        <div className="relative mb-12 w-full">
          <ScrollArea className="w-full whitespace-nowrap pb-4">
            <div className="flex w-max space-x-3 p-1">
              <Link href={`/authors/${author.profileSlug}`}>
                <Badge 
                  variant={!activeCategory ? "default" : "outline"} 
                  className={cn(
                    "cursor-pointer px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-all",
                    !activeCategory ? "bg-primary text-white" : "hover:bg-primary/10 hover:text-primary"
                  )}
                >
                  All Reports
                </Badge>
              </Link>
              
              {categories.map((cat) => (
                <Link 
                  key={cat} 
                  href={`/authors/${author.profileSlug}?category=${encodeURIComponent(cat)}`}
                >
                  <Badge 
                    variant={activeCategory === cat ? "default" : "outline"} 
                    className={cn(
                      "cursor-pointer px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-all",
                      activeCategory === cat ? "bg-primary text-white" : "hover:bg-primary/10 hover:text-primary"
                    )}
                  >
                    {cat}
                  </Badge>
                </Link>
              ))}
            </div>
            <ScrollBar orientation="horizontal" className="h-2 mt-2" />
          </ScrollArea>
        </div>
      )}

      <h2 className="text-2xl md:text-3xl font-bold mb-10 font-headline flex items-center gap-4">
        <span className="h-8 w-1 bg-primary rounded-full"></span>
        {activeCategory ? `Latest in ${activeCategory}` : `Reporting by ${firstName}`}
      </h2>

      {filteredArticles.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10">
          {filteredArticles.map(article => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      ) : (
        <div className="text-center py-24 bg-accent/30 rounded-3xl border-2 border-dashed border-border/50">
          <p className="text-xl text-muted-foreground italic font-light">
            No reports filed in this category yet.
          </p>
        </div>
      )}
    </div>
  );
}
